import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { Badge, Button, Col, Row, Spinner } from "reactstrap";
import cogoToast from "cogo-toast";
import Swal from "sweetalert2";
import { FaArrowLeft, FaEdit, FaTrash } from "react-icons/fa";
import {
  deleteWashProjectApi,
  getWashProjectDetailsApi,
} from "../../../services/Api";
import { SUCCESS_RESULT } from "../../../Constants";
import {
  canDeleteWashProject,
  canUpdateWashProject,
  formatCell,
  formatMoney,
  washStatusColor,
  washStatusLabel,
} from "./washProjectHelpers";
import "./WashProjects.css";

const Field = ({ label, value, full }) => (
  <Col md={full ? 12 : 6} className="mb-3">
    <div className="wash-label">{label}</div>
    <div style={{ whiteSpace: "pre-wrap" }}>{formatCell(value)}</div>
  </Col>
);

const WashProjectDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const canUpdate = canUpdateWashProject();
  const canDelete = canDeleteWashProject();

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getWashProjectDetailsApi({ id })
      .then((res) => {
        setLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          setRecord(res.data.data || null);
        } else {
          cogoToast.error(res.data?.message || "Failed to load details", {
            hideAfter: 5,
          });
          history.push("/projects/wash");
        }
      })
      .catch((err) => {
        setLoading(false);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong loading details.",
          { hideAfter: 5 }
        );
        history.push("/projects/wash");
      });
  }, [id, history]);

  const handleDelete = () => {
    if (!canDelete || !record?.id) return;
    Swal.fire({
      title: "Deactivate this WASH project?",
      text: "This will mark the project inactive.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, deactivate",
    }).then((result) => {
      if (!result.isConfirmed) return;
      deleteWashProjectApi({ id: record.id })
        .then((res) => {
          if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
            cogoToast.success(res.data.message || "Deactivated", { hideAfter: 3 });
            history.push("/projects/wash");
          } else {
            cogoToast.error(res.data?.message || "Delete failed", { hideAfter: 5 });
          }
        })
        .catch((err) => {
          cogoToast.error(
            err?.response?.data?.message || "Something went wrong deleting.",
            { hideAfter: 5 }
          );
        });
    });
  };

  if (loading) {
    return (
      <div className="container-xl mt-4 text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  if (!record) {
    return (
      <div className="container-xl mt-4 wash-page">
        <div className="wash-card">
          <div className="wash-card__body text-center text-muted py-5">
            Record not found.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xl mt-4 mb-5 wash-page">
      <div className="wash-page__header">
        <div>
          <Button color="link" className="px-0 mb-2" tag={Link} to="/projects/wash">
            <FaArrowLeft className="me-1" /> Back to Wash Projects
          </Button>
          <h1 className="wash-page__title">{formatCell(record.project)}</h1>
          <p className="wash-page__subtitle">
            {formatCell(record.site_name)} · {formatCell(record.region)} /{" "}
            {formatCell(record.district)}
          </p>
          <Badge color={washStatusColor(record.status)} className="mt-1">
            {washStatusLabel(record.status)}
          </Badge>
        </div>
        <div className="d-flex gap-2">
          {canUpdate && (
            <Button
              color="primary"
              outline
              tag={Link}
              to={`/projects/wash/edit/${record.id}`}
            >
              <FaEdit className="me-1" /> Edit
            </Button>
          )}
          {canDelete && (
            <Button color="danger" outline onClick={handleDelete}>
              <FaTrash className="me-1" /> Deactivate
            </Button>
          )}
        </div>
      </div>

      <div className="wash-card mb-3">
        <div className="wash-card__body">
          <h5 className="mb-3">Project identity</h5>
          <Row>
            <Field label="Project" value={record.project} />
            <Field label="Year established" value={record.year_established} />
            <Field label="Water source" value={record.water_source} />
            <Field label="Work type" value={record.work_type} />
            <Field label="Program area" value={record.program_area} />
            <Field label="Donor" value={record.donor} />
            <Field label="Details" value={record.details} full />
          </Row>
        </div>
      </div>

      <div className="wash-card mb-3">
        <div className="wash-card__body">
          <h5 className="mb-3">Technical & budget</h5>
          <Row>
            <Field label="Depth" value={record.depth} />
            <Field label="Yield / volume" value={record.yield_or_volume} />
            <Field label="Water quality" value={record.water_quality} />
            <Field label="Functionality" value={record.functionality} />
            <Field label="Budget value" value={formatMoney(record.budget_value)} />
            <Field label="Total beneficiaries" value={record.total_beneficiaries} />
            <Field label="Beneficiary type" value={record.beneficiary_type} />
            <Field label="Response type" value={record.response_type} />
          </Row>
        </div>
      </div>

      <div className="wash-card mb-3">
        <div className="wash-card__body">
          <h5 className="mb-3">Location</h5>
          <Row>
            <Field label="Region" value={record.region} />
            <Field label="District" value={record.district} />
            <Field label="Site name" value={record.site_name} />
            <Field label="Latitude" value={record.latitude} />
            <Field label="Longitude" value={record.longitude} />
          </Row>
        </div>
      </div>

      <div className="wash-card">
        <div className="wash-card__body">
          <h5 className="mb-3">Notes & audit</h5>
          <Row>
            <Field label="Remarks" value={record.remarks} full />
            <Field
              label="Created by"
              value={
                record.creator
                  ? `${record.creator.name || "—"} (${record.creator.email || "—"})`
                  : null
              }
            />
            <Field
              label="Updated by"
              value={
                record.updater
                  ? `${record.updater.name || "—"} (${record.updater.email || "—"})`
                  : null
              }
            />
            <Field label="Created at" value={record.created_at} />
            <Field label="Updated at" value={record.updated_at} />
          </Row>
        </div>
      </div>
    </div>
  );
};

export default WashProjectDetails;
